import { ShortMsxMpv } from '../../types/ShortMsxMpv'
import {
  CategoryPageSection,
  ProductGridType,
  ProductLaneType,
  ShortProductTileV2
} from '../../types/TemplateConfiguration'
import { GoodBetterBestSection, GoodBetterBestValue } from '../../types/GoodBetterBest'
import { VirtualSection } from '../../types/VirtualSection'
import { isOfTypePageHeader } from '../standardHeroContents/standardHeroContents'
import { AutomatedExperience } from '../../components/AutoGenerate/types/AutomatedExperience'
import { FeatureFlags } from '../../types/Global'
import { shouldUseAutomatedExperience } from '../../components/AutoGenerate/automatedSectionUtil'
import { ProductRecommendationsData } from '../../types/Recommendations'
import { sortProductsByStock } from '../recommendations/productRecommendationsBuilder'

declare global {
  interface Window {
    tracking?: {
      track: (eventName: string, data: Record<string, unknown>) => void
    }
  }
}

interface ProductAnalyticsData {
  product_id: string
  name: string
  core_product_id: string
  position: number
  list_section_id: string
}

export const buildProductAnalyticsData = (props: {
  sections: CategoryPageSection[]
  mpvs: ShortMsxMpv[]
  automatedExperience?: AutomatedExperience | undefined
  featureFlags?: FeatureFlags | undefined
  productRecommendations?: ProductRecommendationsData | undefined
}) => {
  const { sections, mpvs, automatedExperience, featureFlags, productRecommendations } = props
  const result: ProductAnalyticsData[] = []

  let position = 0
  let sectionPosition = 0
  const isAnchorBarEnabled = sections.some((section) => section.type === 'anchor links')

  sections.forEach((section, sectionIndex) => {
    if (isOfTypePageHeader(section.type)) {
      return
    }
    sectionPosition = isAnchorBarEnabled ? sectionIndex + 1 : sectionIndex

    switch (section.type) {
      case 'good better best': {
        const goodBetterBestValues: GoodBetterBestValue[] = [
          (section as GoodBetterBestSection).goodValue,
          (section as GoodBetterBestSection).betterValue,
          (section as GoodBetterBestSection).bestValue
        ]
        goodBetterBestValues.forEach((value) => {
          const mpvId =
            value?.template?.productsList?.selectedProduct?.mpvId ||
            value?.template?.category?.selectedCategory?.mpvId ||
            ''
          const mpv = mpvId ? mpvs.find((mpv) => mpv.mpvId === mpvId) : undefined

          if (mpv) {
            result.push({
              product_id: mpvId,
              name: mpv?.name || value.template?.category?.selectedCategory?.name.value || '',
              core_product_id: mpv?.coreProductId || '',
              position: ++position, // Analytics uses 1-based indexing
              list_section_id: `Category Page:${sectionPosition}` // Analytics uses 1-based indexing
            })
          }
        })
        break
      }
      case 'product grid':
      case 'product grid horizontal':
      case 'product grid horizontal XL':
        ;(section as ProductGridType).items.forEach((item) => {
          if (item.product) {
            const mpvId =
              (item.product as ShortProductTileV2).template?.productsList?.selectedProduct?.mpvId ||
              (item.product as ShortProductTileV2).template?.category?.selectedCategory?.mpvId ||
              ''
            const mpv = mpvId ? mpvs.find((mpv) => mpv.mpvId === mpvId) : undefined
            if (mpv) {
              result.push({
                product_id: mpvId,
                name:
                  mpv?.name ||
                  (item.product as ShortProductTileV2).template?.category?.selectedCategory?.name
                    .value ||
                  '',
                core_product_id: mpv?.coreProductId || '',
                position: ++position, // Analytics uses 1-based indexing
                list_section_id: `Category Page:${sectionPosition}` // Analytics uses 1-based indexing
              })
            }
          }
        })
        break
      case 'product lane':
        {
          const automatedSection = automatedExperience?.sections?.find(
            (autoSection) => autoSection.sectionId === section.sectionId
          )
          const shouldUseAutomatedSectionData =
            automatedSection &&
            shouldUseAutomatedExperience(automatedSection, featureFlags) &&
            section.automationEnabled

          if (shouldUseAutomatedSectionData && automatedSection) {
            automatedSection.items.forEach((item) => {
              result.push({
                product_id: item.mpvId,
                name: item?.productName || '',
                core_product_id: item.productId || '',
                position: ++position, // Analytics uses 1-based indexing
                list_section_id: `Category Page:${sectionPosition}` // Analytics uses 1-based indexing
              })
            })
            break
          }
          const productLaneItems = (section as ProductLaneType).items || []
          productLaneItems.forEach((item) => {
            if (item.product) {
              const mpvId =
                (item.product as ShortProductTileV2).template?.productsList?.selectedProduct
                  ?.mpvId ||
                (item.product as ShortProductTileV2).template?.category?.selectedCategory?.mpvId ||
                ''
              const mpv = mpvId ? mpvs.find((mpv) => mpv.mpvId === mpvId) : undefined

              if (mpv) {
                result.push({
                  product_id: mpvId,
                  name:
                    mpv?.name ||
                    (item.product as ShortProductTileV2).template?.category?.selectedCategory?.name
                      .value ||
                    '',
                  core_product_id: mpv?.coreProductId || '',
                  position: ++position, // Analytics uses 1-based indexing
                  list_section_id: `Category Page:${sectionPosition}` // Analytics uses 1-based indexing
                })
              }
            }
          })
        }
        break
      case 'virtual section': {
        const virtualSection = (section as VirtualSection).value
        const mpvIds = [
          virtualSection?.localizedFields?.find(
            (field) =>
              field.contentful_id === virtualSection?.value.browseOurDesignsMpv?.contentful_id
          )?.mpvId,
          virtualSection?.localizedFields?.find(
            (field) =>
              field.contentful_id === virtualSection?.value.uploadYourDesignMpv?.contentful_id
          )?.mpvId
        ]
        mpvIds.forEach((mpvId) => {
          const mpv = mpvs.find((mpv) => mpv.mpvId === mpvId)
          if (mpvId) {
            result.push({
              product_id: mpvId,
              name: mpv?.name || '',
              core_product_id: mpv?.coreProductId || '',
              position: ++position, // Analytics uses 1-based indexing
              list_section_id: `Category Page:${sectionPosition}` // Analytics uses 1-based indexing
            })
          }
        })
        break
      }
      case 'stock options': {
        const automatedSection = automatedExperience?.sections?.find(
          (autoSection) => autoSection.sectionId === section.sectionId
        )
        const shouldUseAutomatedSectionData =
          automatedSection && shouldUseAutomatedExperience(automatedSection, featureFlags)

        if (shouldUseAutomatedSectionData) {
          automatedSection.items.forEach((item) => {
            result.push({
              product_id: item.mpvId,
              name: item?.productName || '',
              core_product_id: item.productId || '',
              position: ++position, // Analytics uses 1-based indexing
              list_section_id: `Category Page:${sectionPosition}` // Analytics uses 1-based indexing
            })
          })
        }
        break
      }
      case 'bestsellers': {
        const bestsellersProducts = productRecommendations?.bestsellers?.products ?? []
        bestsellersProducts.forEach((product) => {
          result.push({
            product_id: product.mpvId,
            name: product.name,
            core_product_id: product.productKey,
            position: ++position,
            list_section_id: `Category Page:${sectionPosition}`
          })
        })
        break
      }
      case 'new arrivals': {
        const newArrivalsProducts = productRecommendations?.newArrivals?.products ?? []
        const sorted = sortProductsByStock(newArrivalsProducts)
        sorted.forEach((product) => {
          result.push({
            product_id: product.mpvId,
            name: product.name,
            core_product_id: product.productKey,
            position: ++position,
            list_section_id: `Category Page:${sectionPosition}`
          })
        })
        break
      }
      default:
        break
    }
  })

  return result
}

export const trackClickEvent = (props: {
  categoryId: string
  pageZone: string
  ctaValue: string
  destinationUrl: string
  index?: number
  linkType?: string
}) => {
  const { categoryId, pageZone, ctaValue, destinationUrl, index, linkType } = props

  const sourcePagePath = window.location.pathname

  let destinationPagePath = destinationUrl
  if (destinationUrl?.includes('https')) {
    destinationPagePath = new URL(destinationUrl).pathname
  }

  const navigationDetail = `${ctaValue}${index ? `:${index}` : ''}`
  const eventDetail = `${sourcePagePath};${destinationPagePath};${pageZone};${ctaValue}${linkType ? `;${linkType}` : ''}`

  const windowTracking = window.tracking
  if (windowTracking?.track) {
    windowTracking.track('Navigation Clicked', {
      category: 'Category Page',
      label: `Content:${pageZone}`,
      eventDetail,
      navigationDetail,
      pageSection: 'Category Page',
      pageStage: 'Discover',
      pageName: `${categoryId}:Category Page`
    })
  }
}

export const trackMediaViewed = (props: {
  categoryId: string
  videoId: string
  videoUrl: string
  mobileVideoUrl: string
  name: string
  progress: string
}) => {
  const { categoryId, videoId, videoUrl, mobileVideoUrl, name, progress } = props

  const windowTracking = window.tracking
  if (windowTracking?.track) {
    windowTracking.track('Media Engaged', {
      category: 'Video',
      label: videoId, // Vista [VIDEO ID]- Unique Identifier
      eventDetail: 'viewed_' + progress,
      pageSection: 'Category Page',
      pageStage: 'Discover',
      pageName: `${categoryId}:Category Page`,
      name,
      videoUrl,
      mobileVideoUrl
    })
  }
}

export const trackMediaClicked = (props: {
  categoryId: string
  videoId: string
  videoUrl: string
  mobileVideoUrl: string
  name: string
  action: string
}) => {
  const { categoryId, videoId, videoUrl, mobileVideoUrl, name, action } = props

  const windowTracking = window.tracking
  if (windowTracking?.track) {
    windowTracking.track('Media Engaged', {
      category: 'Video',
      label: videoId, // Vista [VIDEO ID]- Unique Identifier
      eventDetail: 'clicked_' + action,
      pageSection: 'Category Page',
      pageStage: 'Discover',
      pageName: `${categoryId}:Category Page`,
      name,
      videoUrl,
      mobileVideoUrl
    })
  }
}

export const trackTemplateTileClickEvent = (props: {
  categoryId: string
  pageZone: string
  ctaValue: string
  destinationUrl: string
  index?: number
  linkType?: string
  itemsCount?: number
  sectionTypeCount?: number
}) => {
  const {
    categoryId,
    pageZone,
    ctaValue,
    destinationUrl,
    index,
    linkType,
    itemsCount,
    sectionTypeCount
  } = props

  const sourcePagePath = window.location.pathname

  let destinationPagePath = destinationUrl
  if (destinationUrl?.includes('https')) {
    destinationPagePath = new URL(destinationUrl).pathname
  }

  const navigationDetail = `${index ? `${index}:${itemsCount}:${sectionTypeCount}` : ''}`
  const eventDetail = `${sourcePagePath};${destinationPagePath};${pageZone};${ctaValue}${linkType ? `;${linkType}` : ''}`

  const windowTracking = window.tracking
  if (windowTracking?.track) {
    windowTracking.track('Navigation Clicked', {
      category: 'Category Page',
      label: `Content:${pageZone}`,
      eventDetail,
      navigationDetail,
      pageSection: 'Category Page',
      pageStage: 'Discover',
      pageName: `${categoryId}:Category Page`
    })
  }
}

export const trackCarouselClick = (props: {
  categoryId: string
  pageZone: string
  slideIndex: number
}) => {
  const { categoryId, pageZone, slideIndex } = props

  const eventDetail = `Navigated to carousel slide ${slideIndex}`

  const windowTracking = window.tracking
  if (windowTracking?.track) {
    windowTracking.track('Carousel Arrow Clicked', {
      category: 'Category Page',
      label: `Content:${pageZone}`,
      eventDetail,
      pageSection: 'Category Page',
      pageStage: 'Discover',
      pageName: `${categoryId}:Category Page`
    })
  }
}

export const trackAnchorBarClickEvent = (props: { categoryId: string; label: string }) => {
  const { categoryId } = props
  const windowTracking = window.tracking

  if (windowTracking?.track) {
    windowTracking.track('Link Clicked', {
      category: 'Category Page',
      label: 'Anchor Bar Link',
      pageSection: 'Category Page',
      pageStage: 'Discover',
      pageName: `${categoryId}:Category Page`
    })
  }
}

export const trackNavigationClicks = (props: {
  categoryId: string
  pageZone: string
  destinationUrl: string
  tabIndex: number
  templateIndex?: number
  designId?: string
  isDesignTemplateCTA?: boolean
  isStockOptionsImage?: boolean
  stockOptionsCtaText?: string
}) => {
  const {
    categoryId,
    pageZone,
    destinationUrl,
    tabIndex,
    templateIndex,
    designId,
    isDesignTemplateCTA,
    isStockOptionsImage,
    stockOptionsCtaText
  } = props

  const sourcePagePath = window.location.pathname

  let destinationPagePath = destinationUrl
  if (destinationUrl?.includes('https')) {
    destinationPagePath = new URL(destinationUrl).pathname
  }

  let navigationDetail = `${isDesignTemplateCTA ? 'Browse all templates:' : ''}${tabIndex}${
    templateIndex ? `:${templateIndex}` : ''
  }${designId ? `:${designId}` : ''}`

  if (isStockOptionsImage) {
    navigationDetail = `${categoryId}: ${navigationDetail}`
  }

  if (stockOptionsCtaText) {
    navigationDetail = `${stockOptionsCtaText}: ${tabIndex}`
  }

  const eventDetail = `${sourcePagePath};${destinationPagePath};${pageZone};${tabIndex}${
    templateIndex ? `:${templateIndex}` : ''
  }${designId ? `:${designId}` : ''}`

  const windowTracking = window.tracking
  if (windowTracking?.track) {
    windowTracking.track('Navigation Clicked', {
      category: 'Category Page',
      label: `Content:${pageZone}`,
      eventDetail,
      navigationDetail,
      pageSection: 'Category Page',
      pageStage: 'Discover',
      pageName: `${categoryId}:Category Page`
    })
  }
}

export const trackTabSelected = (props: {
  categoryId: string
  pageZone: string
  tabIndex: string | null
}) => {
  const { categoryId, pageZone, tabIndex } = props

  const windowTracking = window.tracking
  if (windowTracking?.track) {
    windowTracking.track('Tab Selected', {
      category: pageZone,
      label: `${pageZone}:${tabIndex}`,
      pageSection: 'Category Page',
      pageStage: 'Discover',
      pageName: `${categoryId}:Category Page`
    })
  }
}
